// src/serviceWorkerRegistration.ts
// SW registration + update detection. Works best on deployed HTTPS sites.
//
// Web-only in practice: the packaged desktop app loads from file://
// (desktop/main.js -> mainWindow.loadFile), and Chromium does not run service
// workers on file:// origins. Desktop offline support comes from electron-store
// via src/durableStore.ts and updates come from electron-updater — nothing in
// this file affects either.

type UpdateCallback = () => void;

// Background poll. The browser also revalidates the script on navigation, so
// this mainly covers the long-lived tab that never navigates — which is the
// normal way this app is used.
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

let waitingWorker: ServiceWorker | null = null;
let reloading = false;

export function register(onUpdateAvailable?: UpdateCallback) {
  if (!("serviceWorker" in navigator)) return;

  // Whether this document was already under a worker's control when it loaded.
  // This distinguishes the two ways controllerchange can fire:
  //   - first ever install: no controller, the worker activates straight away
  //     and clients.claim() fires controllerchange. The page is already running
  //     the newest build, so reloading would be a pointless bounce on every
  //     first visit.
  //   - an update was applied (here or in another tab): there was a controller,
  //     the document is now stale, and reloading is the whole point.
  const hadControllerAtStart = !!navigator.serviceWorker.controller;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        watchForUpdates(reg, onUpdateAvailable);
      })
      .catch(() => {
        // Some dev hosts (like CodeSandbox preview) may not support SW well.
      });
  });

  // Fires when a new worker takes control, i.e. after applyUpdate() below (or
  // after another tab applied it). Reload once so the document and its assets
  // all come from the same build. The flag matters: without it the reload can
  // re-enter and loop.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadControllerAtStart) return;
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

function watchForUpdates(
  reg: ServiceWorkerRegistration,
  onUpdateAvailable?: UpdateCallback
) {
  // A worker installed while the tab was closed is already parked in "waiting",
  // and updatefound will never fire for it. Catch that case on load.
  if (reg.waiting && navigator.serviceWorker.controller) {
    waitingWorker = reg.waiting;
    onUpdateAvailable?.();
  }

  reg.addEventListener("updatefound", () => {
    const installing = reg.installing;
    if (!installing) return;

    installing.addEventListener("statechange", () => {
      if (installing.state !== "installed") return;

      // No existing controller means this is the first install on this device,
      // not an update — the precache just populated silently and there is
      // nothing for the user to refresh into.
      if (!navigator.serviceWorker.controller) return;

      waitingWorker = installing;
      onUpdateAvailable?.();
    });
  });

  const check = () => {
    void reg.update().catch(() => {
      /* offline, or the check raced a teardown — the next one will retry */
    });
  };

  check();
  window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
}

/**
 * Apply a pending update. The worker responds to SKIP_WAITING by activating,
 * which fires controllerchange and triggers the one-shot reload above.
 */
export function applyUpdate() {
  const worker = waitingWorker;
  if (!worker) {
    // Nothing parked (shouldn't happen if the pill is visible) — a plain
    // reload is still the right thing to do for the user.
    window.location.reload();
    return;
  }
  worker.postMessage({ type: "SKIP_WAITING" });
}

export function unregister() {
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister());
  });
}
